/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer - IBL
 * @Email : kpozos@ibl.mx
 *
 * SpecialPermissionAddEmployeesApp
 *
 * Handles logic for assigning special permissions to selected employees.
 * Integrates with Redux to fetch counts, assign permissions, and update state.
 *
 * @param {Object} props - Component properties
 * @param {Array<Object>} props.selected - List of selected employees
 * @param {Function} props.setSelected - Updates selected employees list
 * @param {Function} props.registerAction - Registers actions in the parent context/view
 * @param {Function} props.setModified - Marks parent state as modified after assignment
 *
 * @returns {Object} Helpers for UI layer
 * @returns {Function} returns.t - Translation function
 */
import {useTranslation} from "react-i18next";
import React, {useEffect} from "react";
import {ATTENDANCE_SUBVIEW_MODEL} from "../../../Model/Model";
import {
    CountSpecialPermissionAssigned,
    CreateMultipleSpecialPermission
} from "../../../../../../../reduxToolkit/UserSpecialPermission/UserSpecialPermissionApi";
import {useDispatch, useSelector} from "react-redux";
import {toast} from "react-toastify";
import {errorMsg} from "../../../../../../../Apis/functions";
import {resetGetSpecialPermissionAssigned} from "../../../../../../../reduxToolkit/UserSpecialPermission/UserSpecialPermissionSlice";

const SpecialPermissionAddEmployeesApp = ({ selected, setSelected, registerAction, setModified }) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const { getSpecialPermissionAssigned } = useSelector((state) => state?.UserSpecialPermissionSlice);

    /**
     * Fetches the total count of assigned special permissions if not already loaded.
     *
     * Triggered when `getSpecialPermissionAssigned` changes.
     * Prevents unnecessary API calls by checking existing data.
     */
    useEffect(() => {
        if(!getSpecialPermissionAssigned?.totalElements)
            dispatch(CountSpecialPermissionAssigned());

    }, [getSpecialPermissionAssigned])

    /**
     * Registers or clears the assign action depending on selected users.
     *
     * - If there are selected users, registers `handleAssign` action
     * - Otherwise, clears the action
     *
     * This allows parent components to trigger assignment dynamically.
     */
    useEffect(() => {
        if(selected.length > 0)
            registerAction(
                ATTENDANCE_SUBVIEW_MODEL.MANAGE.SPECIAL_PERMISSION,
                { handleAssign }
            );
        else {
            registerAction(
                ATTENDANCE_SUBVIEW_MODEL.MANAGE.SPECIAL_PERMISSION,
                {}
            );
        }
    }, [selected]);

    /**
     * Assigns special permissions to selected users.
     *
     * Behavior:
     * - Dispatches API call with selected user IDs
     * - On success:
     *    - Marks state as modified
     *    - Clears selection
     *    - Refreshes assigned permissions count
     *    - Shows success notification
     * - On failure:
     *    - Displays error message based on response code
     *
     * @returns {void}
     */
    const handleAssign = () => {
        dispatch(CreateMultipleSpecialPermission(selected.map(e => e.userId)))
            .then(({ payload: { data: { code, success} } }) => {
            if (success) {
                setModified(true);
                setSelected([]);
                dispatch(resetGetSpecialPermissionAssigned());
                dispatch(CountSpecialPermissionAssigned());
                toast.success(t('has_been_assigned_successfully'));
            } else {
                errorMsg(code);
            }
        });
    };

    return {
        t
    }

}

export default SpecialPermissionAddEmployeesApp
